import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AlertController, ModalController, NavController, PopoverController } from '@ionic/angular';
import { Chart, ChartDataset, ChartOptions } from 'chart.js';
import * as _ from 'lodash';
import moment from 'moment-timezone';

import {
  ActionDropdownComponent,
  ActionDropdownType,
} from 'src/app/components/action-dropdown/action-dropdown.component';
import { DatetimeModalComponent } from 'src/app/components/datetime-modal/datetime-modal.component';
import { FilterOption, FilterValue, SelectedFilterMap } from 'src/app/components/filter-button/filter-button.component';
import {
  CHART_DATE_DIMENSIONS,
  CONVERSATION_PRIORITY_VALUES,
  CONVERSATION_STATUS,
  DATE_OPTIONS,
  DATE_RANGE_TYPES,
  GROUP_BY_OPTIONS,
  routes,
  STATUS_COLUMN_FORMAT_MAP,
  UNIT,
} from 'src/app/constants';
import { getDatesFromDateRange, getRandomColor, downloadCSV as utilDownloadCsv } from 'src/app/utils/utils';
import { CollaboratorType, Customer, Organizer, Profile, SavedReport } from 'src/models';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { CollaboratorTypesService } from 'src/services/collaborator-types.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

const CHART_TYPE_VALUES = {
  TOTAL_COUNT: 'totalCount',
  RESPONSE_TIME_MEAN: 'responseTimeMean',
  RESPONSE_TIME_WORKING_MEAN: 'responseTimeWorkingMean',
  RESOLUTION_TIME_MEAN: 'resolutionTimeMean',
  RESOLUTION_TIME_WORKING_MEAN: 'resolutionTimeWorkingMean',
  RESPONSE_TIME_MEDIAN: 'responseTimeMedian',
  RESPONSE_TIME_WORKING_MEDIAN: 'responseTimeWorkingMedian',
  RESOLUTION_TIME_MEDIAN: 'resolutionTimeMedian',
  RESOLUTION_TIME_WORKING_MEDIAN: 'resolutionTimeWorkingMedian',
  CLOSED_COUNT: 'conversationsResolved',
  SENTIMENT_SCORE_MEAN: 'sentimentScoreMean',
  RESPONSE_TIME_P95: 'responseTimeP95',
  RESPONSE_TIME_WORKING_P95: 'responseTimeWorkingP95',
  RESPONSE_TIME_P99: 'responseTimeP99',
  RESPONSE_TIME_WORKING_P99: 'responseTimeWorkingP99',
  RESOLUTION_TIME_P95: 'resolutionTimeP95',
  RESOLUTION_TIME_WORKING_P95: 'resolutionTimeWorkingP95',
  RESOLUTION_TIME_P99: 'resolutionTimeP99',
  RESOLUTION_TIME_WORKING_P99: 'resolutionTimeWorkingP99',
  WAITING_ON_AGENT_TIME_MEAN: 'waitingOnAgentTimeMean',
  WAITING_ON_AGENT_TIME_WORKING_MEAN: 'waitingOnAgentTimeWorkingMean',
  WAITING_ON_AGENT_TIME_MEDIAN: 'waitingOnAgentTimeMedian',
  WAITING_ON_AGENT_TIME_WORKING_MEDIAN: 'waitingOnAgentTimeWorkingMedian',
  WAITING_ON_AGENT_TIME_P95: 'waitingOnAgentTimeP95',
  WAITING_ON_AGENT_TIME_WORKING_P95: 'waitingOnAgentTimeWorkingP95',
  WAITING_ON_AGENT_TIME_P99: 'waitingOnAgentTimeP99',
  WAITING_ON_AGENT_TIME_WORKING_P99: 'waitingOnAgentTimeWorkingP99',
  WAITING_ON_CUSTOMER_TIME_MEAN: 'waitingOnCustomerTimeMean',
  WAITING_ON_CUSTOMER_TIME_WORKING_MEAN: 'waitingOnCustomerTimeWorkingMean',
  WAITING_ON_CUSTOMER_TIME_MEDIAN: 'waitingOnCustomerTimeMedian',
  WAITING_ON_CUSTOMER_TIME_WORKING_MEDIAN: 'waitingOnCustomerTimeWorkingMedian',
  WAITING_ON_CUSTOMER_TIME_P95: 'waitingOnCustomerTimeP95',
  WAITING_ON_CUSTOMER_TIME_WORKING_P95: 'waitingOnCustomerTimeWorkingP95',
  WAITING_ON_CUSTOMER_TIME_P99: 'waitingOnCustomerTimeP99',
  WAITING_ON_CUSTOMER_TIME_WORKING_P99: 'waitingOnCustomerTimeWorkingP99',
};

@Component({
  selector: 'app-analytics-response-times',
  templateUrl: './analytics-response-times.component.html',
  styleUrls: ['./analytics-response-times.component.scss'],
})
export class AnalyticsResponseTimesComponent implements OnInit {
  @ViewChild('chart') chart: Chart;

  CHART_TYPES = [
    CHART_TYPE_VALUES.TOTAL_COUNT,
    CHART_TYPE_VALUES.RESPONSE_TIME_MEAN,
    CHART_TYPE_VALUES.RESPONSE_TIME_MEDIAN,
    CHART_TYPE_VALUES.RESPONSE_TIME_P95,
    CHART_TYPE_VALUES.RESPONSE_TIME_P99,
    CHART_TYPE_VALUES.RESOLUTION_TIME_MEAN,
    CHART_TYPE_VALUES.RESOLUTION_TIME_MEDIAN,
    CHART_TYPE_VALUES.RESOLUTION_TIME_P95,
    CHART_TYPE_VALUES.RESOLUTION_TIME_P99,
    CHART_TYPE_VALUES.CLOSED_COUNT,
    CHART_TYPE_VALUES.SENTIMENT_SCORE_MEAN,
    CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_MEAN,
    CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_MEDIAN,
    CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_P95,
    CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_P99,
    CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_MEAN,
    CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_MEDIAN,
    CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_P95,
    CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_P99,
    CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_MEAN,
    CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_MEDIAN,
    CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_P95,
    CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_P99,
  ];
  DIMENSION_OPTIONS = [
    {
      label: 'Overall',
      key: undefined,
      icon: 'pulse',
    },
    {
      label: this.authService.tenant.accountsEnabled ? 'Channel' : 'Customer',
      key: 'customerId',
      icon: this.authService.tenant.accountsEnabled ? 'chatbubble' : 'business',
    },
    {
      label: 'Assignee',
      key: 'assigneeId',
      icon: 'person',
    },
    {
      label: 'Tag',
      key: 'tagId',
      icon: 'pricetag',
    },
    {
      label: this.authService.tenant.accountsEnabled ? 'Channel Tag' : 'Customer Tag',
      key: 'customerTagId',
      icon: 'pricetag',
    },
    {
      label: 'Source',
      key: 'sourceType',
      icon: 'chatbubbles',
    },
    {
      label: 'Status Type',
      key: 'status',
      icon: 'pulse',
    },
    {
      label: 'Status',
      key: 'subStatusKey',
      icon: 'pulse',
    },
    {
      label: 'Ticket Type',
      key: 'ticketTypeId',
      icon: 'ticket',
    },
    {
      label: 'Priority',
      key: 'priority',
      icon: 'alert-circle',
    },
    {
      label: 'Project',
      key: 'projectId',
      icon: 'folder-open',
    },
    this.authService.tenant.accountsEnabled
      ? {
          label: 'Account',
          key: 'accountId',
          icon: 'business',
        }
      : undefined,
    this.authService.tenant.accountsEnabled
      ? {
          label: 'Account Tag',
          key: 'accountTagId',
          icon: 'pricetag',
        }
      : undefined,
    {
      label: 'Submitter',
      key: 'submitterUserId',
      icon: 'person',
    },
  ].filter((o) => o !== undefined);
  chartType: string;
  chartStyle: 'bar' | 'line' = 'line';
  dateRange = DATE_OPTIONS[1];
  dimensionKey = this.DIMENSION_OPTIONS[0].key;
  dimensionLabel = this.DIMENSION_OPTIONS[0].label;
  groupByFilter: moment.unitOfTime.Diff = GROUP_BY_OPTIONS[1].key as moment.unitOfTime.Diff;
  groupByFilterLabel = GROUP_BY_OPTIONS[1].label;
  customerFilter: Customer;
  assigneeFilter: Profile;
  startDate: Date | null;
  endDate: Date | null;
  isDateRangeInvalid = false;
  filterCustomers: Organizer[] | Profile[] = [];
  filters: SelectedFilterMap = new Map();
  timezone: string;
  isLoading = false;
  columns = [];
  lookups = {};
  tabularData = [];
  tabularDataWithTotals = [];
  savedReports: SavedReport[] = [];
  showSaveReportModal = false;
  savedReportName = '';
  savedReportShouldShare = false;
  isSaving = false;
  savedReportId: string | null = null;

  businessHoursOnly = true;
  chartSmoothingEnabled = true;
  shouldShowLegend = false;

  unit = UNIT.MINUTES;

  filterOptions: FilterOption[] = [
    {
      label: 'Assignee',
      id: 'assigneeId',
      searchId: 'assignedToUserId',
      icon: 'person',
      type: 'select',
    },
    {
      label: this.authService.tenant.accountsEnabled ? 'Channel' : 'Customer',
      id: 'customerId',
      icon: 'business',
      type: 'select',
    },
    {
      label: 'Submitter',
      id: 'submitterUserId',
      icon: 'person',
      type: 'select',
    },
    {
      label: 'Status Type',
      id: 'status',
      icon: 'pulse',
      type: 'select',
    },
    {
      label: 'Status',
      id: 'subStatusKey',
      searchId: 'subStatus.key',
      icon: 'pulse',
      type: 'select',
    },
    {
      label: 'Source',
      id: 'sourceType',
      icon: 'log-in',
      type: 'select',
      values: [
        {
          label: 'Slack',
          id: 'slack',
        },
        {
          label: 'Email',
          id: 'email',
        },
        {
          label: 'In-app',
          id: 'widget',
        },
        {
          label: 'Dashboard',
          id: 'dashboard',
        },
        {
          label: 'Microsoft Teams',
          id: 'microsoft-teams',
        },
      ],
    },
    {
      label: 'Ticket Type',
      id: 'ticketTypeId',
      icon: 'ticket',
      type: 'select',
    },
    {
      label: 'Tag',
      id: 'tagId',
      searchId: 'tags.id',
      icon: 'pricetag',
      type: 'select',
    },
    {
      label: this.authService.tenant.accountsEnabled ? 'Channel Tag' : 'Customer Tag',
      id: 'customerTagId',
      searchId: 'customer.tags.id',
      icon: 'pricetag',
      type: 'select',
    },
    {
      label: 'Priority',
      id: 'priority',
      icon: 'alert-circle',
      type: 'select',
      values: [
        {
          label: 'Low',
          id: '3',
        },
        {
          label: 'Medium',
          id: '5',
        },
        {
          label: 'High',
          id: '7',
        },
        {
          label: 'Critical',
          id: '9',
        },
      ],
    },
    !this.authService.selectedProject
      ? {
          label: 'Project',
          id: 'projectId',
          icon: 'folder-open',
          type: 'select',
        }
      : undefined,
    this.authService.tenant.accountsEnabled
      ? {
          label: 'Account',
          id: 'accountId',
          icon: 'business',
          type: 'select',
        }
      : undefined,
    this.authService.tenant.accountsEnabled
      ? {
          label: 'Account Tag',
          id: 'accountTagId',
          icon: 'pricetag',
          type: 'select',
        }
      : undefined,
  ].filter((o) => o !== undefined) as FilterOption[];

  collaboratorTypes: Partial<CollaboratorType>[] = [];

  public timeSeriesData: any[] = [];
  public dimensionValues: any[];
  public chartData: ChartDataset[] = [];
  public chartLabels: any[] = [];
  public chartOptions: ChartOptions = {
    responsive: true,
    animation: false,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: this.shouldShowLegend,
        position: 'bottom',
      },
    },
    scales: {
      x: {
        offset: false,
        grid: {
          display: false,
        },
      },
      y: {
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  public getRowClass = (row) => {
    return {
      'totals-row': row.isTotalsRow,
    };
  };

  constructor(
    private popoverCtrl: PopoverController,
    private modalCtrl: ModalController,
    private navController: NavController,
    private apiService: ApiService,
    private route: ActivatedRoute,
    private alertCtrl: AlertController,
    private msgSrvc: MessageService,
    private collaboratorTypesService: CollaboratorTypesService,
    private searchablePopoverService: SearchablePopoverService,
    private authService: AuthService,
  ) {}

  ngOnInit() {
    this.timezone = moment.tz.guess();
    this.chartType = this.CHART_TYPES[0];
    this.endDate = moment().subtract(1, 'day').endOf('day').toDate();
    this.startDate = moment(this.endDate).startOf('day').subtract(7, 'days').toDate();
    this.savedReportId = this.route.snapshot.paramMap.get('reportId');

    if (this.savedReportId) {
      this.runSavedReport();
      return;
    }

    const queryParamDimension = this.route.snapshot.queryParamMap.get('dimension');
    const queryParamChartType = this.route.snapshot.queryParamMap.get('chartType');
    const queryParamBusinessHoursOnly = this.route.snapshot.queryParamMap.get('businessHoursOnly');
    const queryParamGroupBy = this.route.snapshot.queryParamMap.get('groupBy');
    const queryParamDateRange = this.route.snapshot.queryParamMap.get('dateRange');
    const queryParamStartDate = this.route.snapshot.queryParamMap.get('startDate');
    const queryParamEndDate = this.route.snapshot.queryParamMap.get('endDate');
    const queryParamSavedReportId = this.route.snapshot.queryParamMap.get('savedReportId');
    const queryParamUnit = this.route.snapshot.queryParamMap.get('unit');
    const queryParamChartSmoothingEnabled = this.route.snapshot.queryParamMap.get('chartSmoothingEnabled');
    const queryParamFilters = this.route.snapshot.queryParamMap.get('filters');
    const queryParamChartStyle = this.route.snapshot.queryParamMap.get('chartStyle');

    if (queryParamSavedReportId) {
      this.savedReportId = queryParamSavedReportId;
    }

    if (queryParamUnit) {
      this.unit = Array.from(Object.values(UNIT)).find((u) => u.value === Number(queryParamUnit));
    }

    if (queryParamChartSmoothingEnabled) {
      this.chartSmoothingEnabled = queryParamChartSmoothingEnabled === 'true';
    }

    if (queryParamBusinessHoursOnly) {
      this.businessHoursOnly = queryParamBusinessHoursOnly === 'true';
    }

    if (queryParamDimension) {
      this.DIMENSION_OPTIONS.forEach((d) => {
        if (d.key === queryParamDimension) {
          this.dimensionKey = d.key;
          this.dimensionLabel = d.label;
        }
      });
    }

    if (queryParamChartType && this.CHART_TYPES.includes(queryParamChartType)) {
      this.chartType = queryParamChartType;
    }

    if (queryParamGroupBy) {
      GROUP_BY_OPTIONS.forEach((o) => {
        if (o.key === queryParamGroupBy) {
          this.groupByFilter = o.key as moment.unitOfTime.Diff;
          this.groupByFilterLabel = o.label;
        }
      });
    }

    if (queryParamDateRange) {
      let cachedDateRange;

      try {
        cachedDateRange = JSON.parse(queryParamDateRange);
      } catch (e) {
        cachedDateRange = null;
      }

      if (cachedDateRange) {
        this.dateRange = cachedDateRange;
      }

      if (cachedDateRange?.type === DATE_RANGE_TYPES.ABSOLUTE) {
        if (queryParamEndDate) {
          this.endDate = moment(queryParamEndDate).toDate();
        }

        if (queryParamStartDate) {
          this.startDate = moment(queryParamStartDate).toDate();
        }

        this.isDateRangeInvalid = this.startDate.getTime() > this.endDate.getTime();
      }
    }

    if (queryParamFilters) {
      let cachedFilters: Record<string, FilterValue[]> | null;

      try {
        cachedFilters = JSON.parse(queryParamFilters);
      } catch (e) {
        cachedFilters = null;
      }

      if (cachedFilters) {
        this.filters = new Map();

        for (const [key, values] of Object.entries(cachedFilters)) {
          const filter = this.filterOptions.find((o) => o.id === key);

          if (filter) {
            this.filters.set(filter, values);
          }
        }
      }
    }

    if (queryParamChartStyle === 'bar' || queryParamChartStyle === 'line') {
      this.chartStyle = queryParamChartStyle;
    }

    this.getReports();
    this.fetchSavedReports();
    this.loadCollaboratorTypes();
  }

  async fetchSavedReports() {
    this.savedReports = await this.apiService.getPromise('/saved-reports');
  }

  async loadCollaboratorTypes() {
    const collaboratorTypes = await this.collaboratorTypesService.list({
      order: ['name'],
    });

    this.collaboratorTypes = collaboratorTypes.data;

    // TOOD: add this back when the backend supports it
    /*
    for (const collaboratorType of collaboratorTypes.data) {
      this.filterOptions.push({
        label: collaboratorType.name,
        searchId: 'assignedToId',
        id: `collaboratorKeyMap.${collaboratorType.key}`,
        icon: 'person',
        type: 'select',
      } as any);
    }
      */
  }

  storeNavigationState(key: string, value: string) {
    const queryParams: any = {
      [key]: value,
    };

    // Track current view filters and date ranges in the query param so you can share the view directly
    // We don't subscribe to these changes to update the UI because the change detection is too slow
    return this.navController.navigateRoot([], {
      queryParams,
      queryParamsHandling: 'merge',
    });
  }

  onChartTypeChange() {
    this.getReports();
    this.storeNavigationState('chartType', this.chartType);
  }

  setDimension(dimension: { key: string | undefined; label: string }) {
    this.dimensionKey = dimension.key;
    this.dimensionLabel = dimension.label;
    this.getReports();

    this.storeNavigationState('dimension', dimension.key);
  }

  async changeDate(isEnd: boolean) {
    const datePickerModal = await this.modalCtrl.create({
      component: DatetimeModalComponent,
      cssClass: 'datetime-modal',
      componentProps: {
        min: isEnd ? this.startDate : null,
        selected: isEnd ? this.endDate : this.startDate,
        title: isEnd ? 'Select end date' : 'Select start date',
      },
    });

    await datePickerModal.present();

    datePickerModal.onDidDismiss().then((data) => {
      const result = data?.data?.value;

      if (result && result !== 'Invalid date') {
        const newDate = moment(result).toDate();

        if (isEnd) {
          this.endDate = newDate;
        } else {
          this.startDate = newDate;
        }

        this.isDateRangeInvalid = this.startDate.getTime() > this.endDate.getTime();

        if (!this.isDateRangeInvalid) {
          this.storeNavigationState(isEnd ? 'endDate' : 'startDate', moment(newDate).format('YYYY-MM-DD'));
          this.getReports();
        }
      }
    });
  }

  async showDateRangeOptions() {
    this.searchablePopoverService.dateValues({
      event,
      callback: (id: string, _name: string, object: any) => {
        this.dateRange = object;

        this.isDateRangeInvalid =
          this.dateRange.type === DATE_RANGE_TYPES.ABSOLUTE && this.startDate.getTime() > this.endDate.getTime();

        if (!this.isDateRangeInvalid) {
          this.getReports();
          this.storeNavigationState('dateRange', JSON.stringify(this.dateRange));
        }
      },
    });
  }

  async showGroupByOptions() {
    const options: ActionDropdownType[] = GROUP_BY_OPTIONS.map((option) => {
      return {
        label: option.label,
        icon: this.groupByFilter === option.key ? 'checkmark-outline' : 'asdf',
      };
    });

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (idx: number) => {
          popover.dismiss();

          this.groupByFilter = GROUP_BY_OPTIONS[idx].key as moment.unitOfTime.Diff;
          this.groupByFilterLabel = GROUP_BY_OPTIONS[idx].label;

          this.getReports();
          this.storeNavigationState('groupBy', this.groupByFilter);
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  async showChartStyleOptions() {
    const options: ActionDropdownType[] = [
      {
        label: 'Line',
        icon: 'analytics',
      },
      {
        label: 'Bar',
        icon: 'stats-chart',
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (idx: number) => {
          popover.dismiss();

          if (idx === 0) {
            this.chartStyle = 'line';
          } else {
            this.chartStyle = 'bar';
          }

          this.storeNavigationState('chartStyle', this.chartStyle);
          this.getReports();
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  toggleBusinessHoursOnly() {
    this.businessHoursOnly = !this.businessHoursOnly;

    this.getReports();
    this.storeNavigationState('businessHoursOnly', this.businessHoursOnly.toString());
  }

  toggleChartSmoothing() {
    this.chartSmoothingEnabled = !this.chartSmoothingEnabled;

    this.getReports();
    this.storeNavigationState('chartSmoothingEnabled', this.chartSmoothingEnabled.toString());
  }

  toggleLegendShowing() {
    this.shouldShowLegend = !this.shouldShowLegend;
    this.chartOptions = {
      ...this.chartOptions,
      plugins: {
        ...this.chartOptions.plugins,
        legend: {
          display: this.shouldShowLegend,
          position: 'bottom',
        },
      },
    };
    // this.chartOptions.plugins.legend.display = this.shouldShowLegend;
  }

  getReportingQuery() {
    const params: any = {
      dateDimension: this.groupByFilter,
      metric: this.chartType,
      timezone: this.timezone,
      filters: {},
    };

    if (this.businessHoursOnly) {
      if (this.chartType === CHART_TYPE_VALUES.RESPONSE_TIME_MEAN) {
        params.metric = CHART_TYPE_VALUES.RESPONSE_TIME_WORKING_MEAN;
      } else if (this.chartType === CHART_TYPE_VALUES.RESOLUTION_TIME_MEAN) {
        params.metric = CHART_TYPE_VALUES.RESOLUTION_TIME_WORKING_MEAN;
      } else if (this.chartType === CHART_TYPE_VALUES.RESPONSE_TIME_MEDIAN) {
        params.metric = CHART_TYPE_VALUES.RESPONSE_TIME_WORKING_MEDIAN;
      } else if (this.chartType === CHART_TYPE_VALUES.RESOLUTION_TIME_MEDIAN) {
        params.metric = CHART_TYPE_VALUES.RESOLUTION_TIME_WORKING_MEDIAN;
      } else if (this.chartType === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_MEAN) {
        params.metric = CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_MEAN;
      } else if (this.chartType === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_MEDIAN) {
        params.metric = CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_MEDIAN;
      } else if (this.chartType === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_P95) {
        params.metric = CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_P95;
      } else if (this.chartType === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_P99) {
        params.metric = CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_P99;
      }
    }

    if (this.dateRange.type === DATE_RANGE_TYPES.RELATIVE) {
      params.dateRange = this.dateRange;
    } else {
      params.dateRange = {
        type: DATE_RANGE_TYPES.ABSOLUTE,
        startDate: moment(this.startDate).format('YYYY-MM-DD'),
        endDate: moment(this.endDate).format('YYYY-MM-DD'),
      };
    }

    if (this.dimensionKey) {
      params.dimensions = [this.dimensionKey];
    }

    for (const [option, values] of this.filters.entries()) {
      params.filters[option.id] = values.map((value) => value.id);
    }

    if (this.authService.selectedProject) {
      params.filters['projectId'] = [this.authService.selectedProject.id];
    }

    return params;
  }

  async getReports() {
    this.isLoading = true;

    const params = this.getReportingQuery();
    const promises: any[] = [];

    promises.push(this.apiService.postPromise(`/reporting/time-series`, params));

    if (this.dimensionKey) {
      promises.push(
        this.apiService.postPromise(`/reporting/time-series`, {
          ...params,
          dimensions: [],
        }),
      );
    }

    let [rawData, totalsData] = await Promise.all(promises);
    this.columns = rawData.dates;
    this.lookups = rawData.lookups;
    this.timeSeriesData = rawData.data;
    this.tabularData = (this.dimensionKey ? Object.keys(this.timeSeriesData) : [undefined])
      .map((value) => {
        const row = {
          dimensionLabel: this.getValue(value, this.dimensionKey),
          dimension: value,
        };

        for (const date of this.columns) {
          row[date] = this.getCellValue(value, date, this.timeSeriesData);
        }

        return row;
      })
      .filter((row) => {
        if (this.dimensionKey === 'tagId') {
          return row.dimension !== 'null';
        }

        return true;
      });

    if (totalsData?.data) {
      const totalsRow = {
        dimensionLabel: 'Total',
        dimension: undefined,
        isTotalsRow: true,
      };

      for (const date of this.columns) {
        totalsRow[date] = this.getCellValue(undefined, date, totalsData.data);
      }

      this.tabularDataWithTotals = this.tabularData.concat(totalsRow);
    } else {
      this.tabularDataWithTotals = this.tabularData;
    }

    const labels: Set<string> = new Set();
    const values = new Map();
    const dateFormat = this.getDateFormat();

    for (const row of this.tabularData) {
      const dimension = row.dimension;

      for (const dateString of this.columns) {
        const value = row[dateString] ?? 0;

        labels.add(moment(dateString).format(dateFormat));

        if (!values.get(dimension)) {
          values.set(dimension, []);
        }

        values.get(dimension).push(value);
      }
    }

    this.chartData = [];

    for (const [dimension, v] of values.entries()) {
      const color = this.getColor(dimension);
      this.chartData.push({
        type: this.chartStyle,
        fill: true,
        data: v,
        backgroundColor: (ctx, options) => {
          if (this.chartStyle === 'bar') {
            return color;
          }
          const grad = ctx.chart.ctx.createLinearGradient(0, 0, 0, 600);
          grad.addColorStop(0, color);
          grad.addColorStop(1, '#ffffff00');
          ctx.chart.ctx.fillStyle = grad;
          return grad;
        },
        borderColor: color,
        tension: this.chartSmoothingEnabled ? 0.3 : 0,
        label: this.getValue(dimension, this.dimensionKey).value,
      });
    }

    this.chartLabels = Array.from(labels);
    this.isLoading = false;
  }

  async changeUnit() {
    const units = _.values(UNIT);
    const options: ActionDropdownType[] = units.map((option) => {
      return {
        label: option.label,
        icon: this.unit.value === option.value ? 'checkmark-outline' : 'asdf',
      };
    });

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (idx: number) => {
          popover.dismiss();

          this.unit = units[idx];

          this.getReports();
          this.storeNavigationState('unit', this.unit.value.toString());
        },
      },
      showBackdrop: false,
      event,
    });

    popover.present();
  }

  downloadCSV() {
    const headers = [{ id: 'dimensionLabel', label: 'dimension' }, ...this.columns];
    const today = moment().endOf('day').toDate();

    let label = `report-${this.chartType}-`;

    if (this.dateRange.type === DATE_RANGE_TYPES.ABSOLUTE) {
      label += `${this.startDate.toISOString()}-${this.endDate.toISOString()}`;
    } else {
      label += `${_.kebabCase(this.dateRange.label.toLocaleLowerCase())}-${today.toISOString()}`;
    }

    utilDownloadCsv(this.tabularData, headers, label);
  }

  getYAxisLabel() {
    if (this.chartType === CHART_TYPE_VALUES.TOTAL_COUNT) {
      return 'Conversation count';
    } else if (
      this.chartType === CHART_TYPE_VALUES.RESPONSE_TIME_MEAN ||
      this.chartType === CHART_TYPE_VALUES.RESPONSE_TIME_P95 ||
      this.chartType === CHART_TYPE_VALUES.RESPONSE_TIME_P99 ||
      this.chartType === CHART_TYPE_VALUES.RESPONSE_TIME_MEDIAN
    ) {
      return `First response time (${this.unit.label.toLowerCase()})`;
    } else if (
      this.chartType === CHART_TYPE_VALUES.RESOLUTION_TIME_MEAN ||
      this.chartType === CHART_TYPE_VALUES.RESOLUTION_TIME_P95 ||
      this.chartType === CHART_TYPE_VALUES.RESOLUTION_TIME_P99 ||
      this.chartType === CHART_TYPE_VALUES.RESOLUTION_TIME_MEDIAN
    ) {
      return `Resolution time (${this.unit.label.toLowerCase()})`;
    } else if (this.chartType === CHART_TYPE_VALUES.CLOSED_COUNT) {
      return 'Resolution count';
    } else if (this.chartType === CHART_TYPE_VALUES.SENTIMENT_SCORE_MEAN) {
      return 'Sentiment score';
    } else if (
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_MEAN ||
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_MEDIAN ||
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_P95 ||
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_P99
    ) {
      return `Waiting on agent time (${this.unit.label.toLowerCase()})`;
    } else if (
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_MEAN ||
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_MEDIAN ||
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_P95 ||
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_P99
    ) {
      return `Waiting on customer time (${this.unit.label.toLowerCase()})`;
    }

    return 'No data';
  }

  getValue(value, dimensionKey) {
    if (!dimensionKey) {
      return {
        value: this.getYAxisLabel(),
      };
    }

    const dimensionLookups = this.lookups[dimensionKey];
    const resultValue = dimensionLookups ? dimensionLookups[value] ?? value : value;

    if (dimensionKey === 'subStatusKey') {
      const [type, ...name] = resultValue.split(':');

      return {
        value: name.join(':'),
        icon: STATUS_COLUMN_FORMAT_MAP[type]?.icon,
        iconClass: STATUS_COLUMN_FORMAT_MAP[type]?.iconClass,
      };
    } else if (dimensionKey === 'status') {
      return {
        value: resultValue,
        icon: STATUS_COLUMN_FORMAT_MAP[value]?.icon,
        iconClass: STATUS_COLUMN_FORMAT_MAP[value]?.iconClass,
      };
    } else {
      return {
        value: resultValue,
      };
    }
  }

  getCellValue(value, column, data) {
    const rawMetric = (value != null ? data[value][column] : data[column]) ?? 0;

    return [
      CHART_TYPE_VALUES.TOTAL_COUNT,
      CHART_TYPE_VALUES.CLOSED_COUNT,
      CHART_TYPE_VALUES.SENTIMENT_SCORE_MEAN,
    ].includes(this.chartType)
      ? rawMetric
      : Math.ceil((rawMetric as number) / this.unit.value);
  }

  getColumnName(column) {
    return moment(column).format(this.getDateFormat());
  }

  getDateFormat() {
    if (this.groupByFilter === CHART_DATE_DIMENSIONS.MONTH) {
      return 'MMM YYYY';
    }

    return 'MMM D YYYY';
  }

  filtersDidChange(selected: SelectedFilterMap) {
    this.filters = selected;

    this.getReports();
    this.cacheFilters();
  }

  openSaveReportModal() {
    this.showSaveReportModal = true;
  }

  onDismissSaveReportModal() {
    this.showSaveReportModal = false;
  }

  async saveReport() {
    const result: any = await this.apiService.postPromise('/saved-reports', {
      ...this.getReportingQuery(),
      displaySettings: {
        unitConversion: this.unit.value,
        lineSmoothing: this.chartSmoothingEnabled,
        chartStyle: this.chartStyle,
      },
      name: this.savedReportName,
      userIds: this.savedReportShouldShare ? null : undefined,
    });

    this.savedReportShouldShare = false;
    this.savedReportName = '';
    this.savedReportId = result.id;

    this.onDismissSaveReportModal();

    document.dispatchEvent(new CustomEvent('saved-report', { detail: result }));

    setTimeout(() => {
      this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.ANALYTICS}/performance/reports/${result.id}`);
    }, 300);
  }

  async saveExistingReport(updates: any) {
    const result: any = await this.apiService.putPromise(`/saved-reports/${this.savedReportId}`, updates);

    document.dispatchEvent(new CustomEvent('saved-report', { detail: result }));
  }

  savedReportCompareFn(v1, v2) {
    return v1 === v2 || (v1 === '' && v2 === null);
  }

  async loadLookups(filters?: Record<string, string[]> | null): Promise<Record<string, Record<string, string>>> {
    if (!filters || !Object.keys(filters).length) {
      return {};
    }

    return await this.apiService.postPromise('/saved-filters/get-lookup-values', filters);
  }

  cacheFilters() {
    const cachedFilters: Record<string, FilterValue[]> = {};

    for (const [key, values] of this.filters.entries()) {
      cachedFilters[key.id] = values;
    }

    this.storeNavigationState('filters', JSON.stringify(cachedFilters));
  }

  async runSavedReport() {
    if (!this.savedReports?.length) {
      await this.fetchSavedReports();
    }

    let savedReport = this.savedReports.find((r) => r.id === this.savedReportId);

    if (!savedReport) {
      return;
    }

    this.isLoading = true;

    const loadFiltersPromise = this.loadLookups(savedReport.filters);

    if (
      [
        CHART_TYPE_VALUES.RESPONSE_TIME_WORKING_MEAN,
        CHART_TYPE_VALUES.RESOLUTION_TIME_WORKING_MEAN,
        CHART_TYPE_VALUES.RESPONSE_TIME_WORKING_MEDIAN,
        CHART_TYPE_VALUES.RESOLUTION_TIME_WORKING_MEDIAN,
        CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_MEAN,
        CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_MEDIAN,
        CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_P95,
        CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_P99,
      ].includes(savedReport.metric)
    ) {
      this.businessHoursOnly = true;

      if (savedReport.metric === CHART_TYPE_VALUES.RESPONSE_TIME_WORKING_MEAN) {
        this.chartType = CHART_TYPE_VALUES.RESPONSE_TIME_MEAN;
      } else if (savedReport.metric === CHART_TYPE_VALUES.RESOLUTION_TIME_WORKING_MEAN) {
        this.chartType = CHART_TYPE_VALUES.RESOLUTION_TIME_MEAN;
      } else if (savedReport.metric === CHART_TYPE_VALUES.RESPONSE_TIME_WORKING_MEDIAN) {
        this.chartType = CHART_TYPE_VALUES.RESPONSE_TIME_MEDIAN;
      } else if (savedReport.metric === CHART_TYPE_VALUES.RESOLUTION_TIME_WORKING_MEDIAN) {
        this.chartType = CHART_TYPE_VALUES.RESOLUTION_TIME_MEDIAN;
      } else if (savedReport.metric === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_MEAN) {
        this.chartType = CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_MEAN;
      } else if (savedReport.metric === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_MEDIAN) {
        this.chartType = CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_MEDIAN;
      } else if (savedReport.metric === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_P95) {
        this.chartType = CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_P95;
      } else if (savedReport.metric === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_P99) {
        this.chartType = CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_P99;
      } else if (savedReport.metric === CHART_TYPE_VALUES.RESPONSE_TIME_P95) {
        this.chartType = CHART_TYPE_VALUES.RESPONSE_TIME_P95;
      } else if (savedReport.metric === CHART_TYPE_VALUES.RESPONSE_TIME_P99) {
        this.chartType = CHART_TYPE_VALUES.RESPONSE_TIME_P99;
      } else if (savedReport.metric === CHART_TYPE_VALUES.RESOLUTION_TIME_P95) {
        this.chartType = CHART_TYPE_VALUES.RESOLUTION_TIME_P95;
      } else if (savedReport.metric === CHART_TYPE_VALUES.RESOLUTION_TIME_P99) {
        this.chartType = CHART_TYPE_VALUES.RESOLUTION_TIME_P99;
      }
    } else {
      this.businessHoursOnly = false;
      this.chartType = savedReport.metric;
    }

    await this.storeNavigationState('chartType', this.chartType);
    await this.storeNavigationState('businessHoursOnly', this.businessHoursOnly.toString());

    this.dimensionKey = savedReport.dimensions?.length ? savedReport.dimensions[0] : undefined;
    this.dimensionLabel = this.DIMENSION_OPTIONS.find((d) => d.key === this.dimensionKey)?.label;

    await this.storeNavigationState('dimension', this.dimensionKey);

    this.groupByFilter = savedReport.dateDimension as any;
    this.groupByFilterLabel = GROUP_BY_OPTIONS.find((o) => o.key === this.groupByFilter)?.label;

    await this.storeNavigationState('groupBy', this.groupByFilter);

    this.dateRange = DATE_OPTIONS.find((o) => {
      if (o.type === DATE_RANGE_TYPES.ABSOLUTE && savedReport.dateRange.type === DATE_RANGE_TYPES.ABSOLUTE) {
        return true;
      }

      if (o.type === DATE_RANGE_TYPES.RELATIVE && savedReport.dateRange.type === DATE_RANGE_TYPES.RELATIVE) {
        return (
          o.period === (savedReport.dateRange as any).period &&
          o.startIndex === (savedReport.dateRange as any).startIndex &&
          o.endIndex === (savedReport.dateRange as any).endIndex
        );
      }

      return false;
    });

    if (this.dateRange.type === DATE_RANGE_TYPES.ABSOLUTE) {
      this.startDate = moment((savedReport.dateRange as any).startDate).toDate();
      this.endDate = moment((savedReport.dateRange as any).endDate).toDate();
    }

    await this.storeNavigationState('dateRange', JSON.stringify(this.dateRange));
    await this.storeNavigationState('startDate', moment(this.startDate).format('YYYY-MM-DD'));
    await this.storeNavigationState('endDate', moment(this.endDate).format('YYYY-MM-DD'));

    if (savedReport.displaySettings.unitConversion) {
      this.unit = Array.from(Object.values(UNIT)).find((u) => u.value === savedReport.displaySettings.unitConversion);
    }

    await this.storeNavigationState('unit', this.unit.value.toString());

    if (savedReport.displaySettings.lineSmoothing != null) {
      this.chartSmoothingEnabled = savedReport.displaySettings.lineSmoothing;
    }

    await this.storeNavigationState('chartSmoothingEnabled', this.chartSmoothingEnabled.toString());

    if (savedReport.displaySettings.chartStyle === 'bar' || savedReport.displaySettings.chartStyle === 'line') {
      this.chartStyle = savedReport.displaySettings.chartStyle;
    }

    await this.storeNavigationState('chartStyle', this.chartStyle);

    const filterLookups = await loadFiltersPromise;

    this.filters = new Map();

    for (const [key, values] of Object.entries(savedReport.filters)) {
      const filter = this.filterOptions.find((o) => o.id === key);

      if (filter) {
        this.filters.set(
          filter,
          values.map((v) => ({ id: v, label: filterLookups[key]?.[v] ?? v })),
        );
      }
    }

    await this.cacheFilters();

    this.savedReportId = savedReport.id;

    await this.storeNavigationState('savedReportId', this.savedReportId);

    this.isLoading = false;

    this.getReports();
  }

  async deleteSavedReport(savedReportId: string) {
    await this.apiService.deletePromise(`/saved-reports/${savedReportId}`);
    document.dispatchEvent(new CustomEvent('saved-report', { detail: savedReportId }));

    this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.ANALYTICS}/performance`);
  }

  async showSavedReportOptions(savedReportId: string) {
    const savedReport = this.getSavedReport(savedReportId);

    if (!savedReport) {
      return;
    }

    const ACTION_UPDATE_FILTERS = 'Save filters to existing report';
    const ACTION_CREATE_NEW_SAVED_REPORT = 'Save as new saved report';
    const ACTION_RENAME_SAVED_REPORT = 'Rename';
    const ACTION_REMOVE_SAVED_REPORT = 'Remove saved report';

    const options: ActionDropdownType[] = [
      {
        icon: 'save-outline',
        label: ACTION_UPDATE_FILTERS,
      },
      {
        icon: 'create-outline',
        label: ACTION_RENAME_SAVED_REPORT,
      },
      {
        icon: 'add-circle-outline',
        label: ACTION_CREATE_NEW_SAVED_REPORT,
      },
      {
        icon: 'trash-outline',
        label: ACTION_REMOVE_SAVED_REPORT,
        destructive: true,
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (_idx: number, label: string) => {
          popover.dismiss();

          if (label === ACTION_UPDATE_FILTERS) {
            this.saveExistingReport({
              ...this.getReportingQuery(),
              displaySettings: {
                unitConversion: this.unit.value,
                lineSmoothing: this.chartSmoothingEnabled,
                chartStyle: this.chartStyle,
              },
            });
          }
          if (label === ACTION_CREATE_NEW_SAVED_REPORT) {
            this.openSaveReportModal();
          }
          if (label === ACTION_RENAME_SAVED_REPORT) {
            const confirm = await this.alertCtrl.create({
              header: `Rename Saved Report`,
              inputs: [
                {
                  placeholder: 'Enter saved report name',
                  attributes: {
                    maxLength: 320,
                  },
                  value: savedReport.name,
                },
              ],
              buttons: [
                {
                  text: 'Cancel',
                  handler: () => {},
                },
                {
                  text: 'Save',
                  handler: async (data: any[]) => {
                    let name = data[0];

                    if (!name) {
                      return;
                    }

                    savedReport.name = name;

                    this.savedReports = [
                      ...this.savedReports.map((r) => ({
                        ...r,
                      })),
                    ];

                    this.saveExistingReport({ name });
                  },
                },
              ],
            });

            confirm.present();
          } else if (label === ACTION_REMOVE_SAVED_REPORT) {
            const confirm = await this.alertCtrl.create({
              header: `Delete Saved Report`,
              message: 'Are you sure you want to delete this saved report?',
              buttons: [
                {
                  text: 'Cancel',
                  handler: () => {},
                },
                {
                  text: 'Confirm',
                  handler: async () => {
                    await this.deleteSavedReport(savedReportId);
                  },
                },
              ],
            });

            confirm.present();
          }
        },
      },
      showBackdrop: false,
      event,
    });

    popover.present();
  }

  getSavedReport(savedReportId: string) {
    return this.savedReports.find((r) => r.id === savedReportId);
  }

  getColor(name: string) {
    const map = {
      [CONVERSATION_STATUS.OPEN]: '#cc0022aa',
      [CONVERSATION_STATUS.IN_PROGRESS]: '#cc9922aa',
      [CONVERSATION_STATUS.ON_HOLD]: '#0000ffaa',
      [CONVERSATION_STATUS.CLOSED]: '#00cc22aa',
      [CONVERSATION_PRIORITY_VALUES.LOW]: '#00cc22aa',
      [CONVERSATION_PRIORITY_VALUES.MEDIUM]: '#0000ccaa',
      [CONVERSATION_PRIORITY_VALUES.HIGH]: '#cc9922aa',
      [CONVERSATION_PRIORITY_VALUES.CRITICAL]: '#cc0022aa',
      null: '#88888855',
    };

    return map[name] ?? getRandomColor();
  }

  viewInInbox() {
    // Hacky way to figure out start and end of relative dates
    let startDate: Date;
    let endDate: Date;

    if (this.dateRange.type === 'absolute') {
      startDate = this.startDate;
      endDate = this.endDate;
    } else {
      [startDate, endDate] = getDatesFromDateRange(this.dateRange);
    }

    const filterMap = this.filters;
    filterMap.set(
      {
        icon: 'calendar',
        id: 'createdAt',
        label: 'Date submitted',
        type: 'date',
      },
      [
        { id: startDate.toISOString(), label: moment(startDate).format('MMM Do YYYY') },
        { id: endDate.toISOString(), label: moment(endDate).format('MMM Do YYYY') },
      ],
    );

    // Handle weird case where tagId is used in reports but tags.id is used in the inbox
    for (const [key, values] of filterMap.entries()) {
      if (key.id === 'tagId') {
        key.id = 'tags.id';
      } else if (key.id === 'customerTagId') {
        key.id = 'customer.tags.id';
      } else if (key.id === 'accountTagId') {
        key.id = 'account.tags.id';
      }
    }

    this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.INBOX}/all`, {
      queryParams: {
        filters: JSON.stringify([...filterMap]),
      },
      queryParamsHandling: 'merge',
    });
  }

  copyLink() {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => {
        this.msgSrvc.show('✓ Copied link to clipboard');
      })
      .catch((err) => {
        console.error('Could not copy text', err);
      });
  }
}
